import { demoFunc } from './dees-harness-chat.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessControlStyles } from '../harness.styles.js';
import type {
  IHarnessAccountChangeDetail,
  IHarnessAttachment,
  IHarnessComposerOption,
  IHarnessComposerSuggestion,
  IHarnessMessage,
  IHarnessPermissionRequest,
  IHarnessQuestionRequest,
  IHarnessIntelligenceExchange,
  IHarnessScratchpad,
  IHarnessSessionMetrics,
  IHarnessStatus,
  IHarnessTodoItem,
  IHarnessUsage,
  THarnessIntelligenceAvailabilityStatus,
  THarnessStreamDelta,
} from '../interfaces.js';
import {
  harnessMaxAttachmentBytes,
  harnessMaxTotalAttachmentBytes,
  harnessParseTodos,
} from '../harness.helpers.js';
import { DeesHarnessToolRegistry } from '../toolregistry.js';
import { DeesHarnessMessageList } from '../dees-harness-message-list/dees-harness-message-list.js';
import { DeesHarnessComposer } from '../dees-harness-composer/dees-harness-composer.js';
import {
  DeesHarnessSessionSidebar,
  type THarnessSessionSidebarSection,
} from '../dees-harness-session-sidebar/dees-harness-session-sidebar.js';
import '../dees-harness-todos/dees-harness-todos.js';
import '../dees-harness-usage/dees-harness-usage.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-chat': DeesHarnessChat;
  }
}

export interface IHarnessChatToolbarAction {
  id: string;
  label: string;
  iconName: string;
  action: () => void | Promise<void>;
  tooltip?: string;
  disabled?: boolean;
}

/**
 * The assembled agent chat: optional toolbar, streaming message list,
 * pinned permission queue, status line, and composer. Owns no business
 * logic — consumers feed `.messages`/`.permissions`/`.status` (and stream
 * through `applyDelta()`), and listen for the bubbling harness-* events
 * from the children.
 */
@customElement('dees-harness-chat')
export class DeesHarnessChat extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor messages: IHarnessMessage[] = [];

  @property({ attribute: false })
  accessor permissions: IHarnessPermissionRequest[] = [];

  /** agent questions render inline like permissions; answered ones persist */
  @property({ attribute: false })
  accessor questions: IHarnessQuestionRequest[] = [];

  @property({ attribute: false })
  accessor status: IHarnessStatus = { type: 'idle' };

  @property()
  accessor transcriptKey: string = '';

  @property({ type: Boolean })
  accessor hasEarlierMessages: boolean = false;

  @property({ type: Boolean })
  accessor loadingEarlier: boolean = false;

  @property({ attribute: false })
  accessor usage: IHarnessUsage = {};

  /**
   * Current task list for the side panel. Unless todosAuthoritative is true,
   * an empty list derives from the newest todo tool call in `messages`.
   */
  @property({ attribute: false })
  accessor todos: IHarnessTodoItem[] = [];

  /** Treat an empty host-provided task list as authoritative instead of deriving transcript tasks. */
  @property({ type: Boolean })
  accessor todosAuthoritative: boolean = false;

  @property({ type: Boolean })
  accessor showTodosPanel: boolean = true;

  @property({ type: Boolean })
  accessor showSessionSidebar: boolean = true;

  @property({ attribute: false })
  accessor sessionMetrics: IHarnessSessionMetrics = {};

  @property({ attribute: false })
  accessor scratchpad: IHarnessScratchpad | undefined = undefined;

  @property({ type: Boolean })
  accessor scratchpadBusy: boolean = false;

  @property()
  accessor scratchpadError: string = '';

  @property({ type: Boolean })
  accessor sessionIntelligenceEnabled: boolean = false;

  @property()
  accessor intelligenceAvailabilityStatus: THarnessIntelligenceAvailabilityStatus = 'available';

  @property()
  accessor intelligenceUnavailableReason: string = '';

  @property()
  accessor intelligenceHeading: string = 'Session Intelligence';

  @property({ attribute: false })
  accessor intelligenceExchanges: IHarnessIntelligenceExchange[] = [];

  @property({ type: Boolean })
  accessor intelligenceBusy: boolean = false;

  @property()
  accessor intelligenceError: string = '';

  @state()
  private accessor sessionSidebarOpen: boolean = false;

  @state()
  private accessor desktopSessionSidebarVisible: boolean = true;

  @state()
  private accessor narrowSessionLayout: boolean = false;

  private sessionLayoutResizeObserver: ResizeObserver | undefined;

  @property()
  accessor heading: string = '';

  @property()
  accessor subheading: string = '';

  @property({ attribute: false })
  accessor toolbarActions: IHarnessChatToolbarAction[] = [];

  @property({ type: Boolean })
  accessor showToolbar: boolean = true;

  @property({ type: Boolean })
  accessor showRoles: boolean = true;

  @property({ type: Boolean })
  accessor showTimestamps: boolean = true;

  /** Parse markdown during active streams; disable to parse only at message end. */
  @property({ type: Boolean })
  accessor markdownWhileStreaming: boolean = true;

  @property({ type: Boolean })
  accessor disabled: boolean = false;

  /** Lock composer mutations while preserving readable, copyable draft content. */
  @property({ type: Boolean })
  accessor inputLocked: boolean = false;

  @property({ type: Boolean })
  accessor busy: boolean = false;

  /** Whether the host can abort an in-flight run. */
  @property({ type: Boolean })
  accessor abortEnabled: boolean = true;

  /** Accessible explanation shown when Stop is present but unavailable. */
  @property()
  accessor abortDisabledReason: string = 'Stopping is unavailable.';

  /** queued-message count while busy; enables the composer's "Steer now" */
  @property({ type: Number })
  accessor queuedCount: number = 0;

  /** Whether the host implements the optional harness-steer action. */
  @property({ type: Boolean })
  accessor steeringEnabled: boolean = true;

  @property({ type: Boolean })
  accessor queueingEnabled: boolean = false;

  @property({ type: Boolean })
  accessor inputSteeringEnabled: boolean = false;

  @property()
  accessor composerValue: string = '';

  @property({ attribute: false })
  accessor attachments: IHarnessAttachment[] = [];

  @property({ attribute: false })
  accessor attachmentsEnabled: boolean = true;

  @property({ attribute: false })
  accessor suggestions: IHarnessComposerSuggestion[] = [];

  @property({ type: Number })
  accessor maxAttachmentCount: number = Number.POSITIVE_INFINITY;

  @property({ type: Number })
  accessor maxAttachmentBytes: number = harnessMaxAttachmentBytes;

  @property({ type: Number })
  accessor maxTotalAttachmentBytes: number = harnessMaxTotalAttachmentBytes;

  @property()
  accessor account: string = '';

  @property({ attribute: false })
  accessor accountOptions: IHarnessComposerOption[] = [];

  @property()
  accessor model: string = '';

  @property({ attribute: false })
  accessor modelOptions: string[] = [];

  @property()
  accessor reasoningEffort: string = '';

  @property({ attribute: false })
  accessor effortOptions: string[] = [];

  /** agent mode switcher (e.g. Planning / Building); empty hides it */
  @property()
  accessor mode: string = '';

  @property({ attribute: false })
  accessor modeOptions: string[] = [];

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessControlStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
        height: 100%;
        font-family: var(--dees-font-family);
        color: var(--dees-color-text-primary);
        background: var(--dees-color-bg-primary);
        container-type: inline-size;
      }

      .chatBody {
        position: relative;
        flex: 1;
        min-height: 0;
        min-width: 0;
        display: flex;
      }

      .chatBody > * {
        /* Wide transcript content must scroll or clip inside its column,
           never widen the chat past its pane. */
        min-width: 0;
      }

      .transcriptColumn {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        /* Keep composer/suggestion layers below the adjacent narrow drawer. */
        isolation: isolate;
      }

      .sessionPanel {
        flex: 0 0 260px;
        min-width: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 0;
        border-left: 1px solid var(--dees-color-border-subtle);
        background: var(--dees-color-bg-canvas);
      }

      .sessionPanel.desktopHidden {
        display: none;
      }

      .sessionPanel > dees-harness-session-sidebar {
        flex: 1;
        min-height: 0;
      }

      .sessionPanelClose,
      .sessionPanelToggle,
      .sessionPanelBackdrop {
        display: none;
      }

      .sessionPanelToggle,
      .toolbarSessionPanelToggle {
        box-sizing: border-box;
        width: var(--dees-control-height-md);
        height: var(--dees-control-height-md);
        padding: 0;
        align-items: center;
        justify-content: center;
        border: 1px solid transparent;
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: transparent;
        color: var(--dees-color-text-primary);
        cursor: pointer;
      }

      .toolbarSessionPanelToggle {
        display: flex;
      }

      .sessionPanelToggle:hover,
      .toolbarSessionPanelToggle:hover {
        background: var(--dees-color-badge-default-bg);
      }

      .sessionPanelToggle:focus-visible,
      .toolbarSessionPanelToggle:focus-visible {
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: 2px;
      }

      /* Narrow hosts keep every session section reachable in an overlay.
         Todo tool cards remain a second transcript-level representation. */
      @container (max-width: 680px) {
        .chatBody {
          /* The closed off-canvas drawer must not widen its owning chat. */
          overflow-x: clip;
        }

        .sessionPanel {
          position: absolute;
          inset: 0 0 0 auto;
          display: flex;
          width: min(320px, calc(100% - 36px));
          max-width: none;
          padding: 42px 0 0;
          border-left-color: var(--dees-color-border-default);
          box-shadow: var(--dees-shadow-lg);
          visibility: hidden;
          transform: translateX(100%);
          transition:
            transform var(--dees-transition-default) var(--dees-ease-out),
            visibility 0s linear var(--dees-transition-default);
        }

        .sessionPanel.desktopHidden {
          display: flex;
        }

        .sessionPanel.open {
          visibility: visible;
          transform: translateX(0);
          transition-delay: 0s;
        }

        .sessionPanelClose {
          position: absolute;
          top: 6px;
          right: 8px;
          display: block;
        }

        .sessionPanelToggle {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
        }

        .sessionPanelBackdrop {
          position: absolute;
          inset: 0;
          display: block;
          border: 0;
          background: color-mix(in srgb, var(--dees-color-bg-primary) 58%, transparent);
          opacity: 0;
          pointer-events: none;
          transition: opacity var(--dees-transition-default) var(--dees-ease-standard);
        }

        .sessionPanelBackdrop.open {
          opacity: 1;
          pointer-events: auto;
        }

        @media (prefers-reduced-motion: reduce) {
          .sessionPanel,
          .sessionPanelBackdrop {
            transition: none;
          }
        }
      }

      .toolbar {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-md);
        min-height: 48px;
        box-sizing: border-box;
        padding: 6px 16px;
        border-bottom: 1px solid var(--dees-color-border-subtle);
        background: var(--dees-color-bg-canvas);
        /* chrome floats above the scrolling transcript */
        position: relative;
        z-index: 1;
      }

      .headingBlock {
        min-width: 0;
        flex: 1;
      }

      .headingText {
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size, 13px);
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .subheadingText {
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .toolbarActions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex: 0 0 auto;
      }

      .transcriptColumn dees-harness-message-list {
        flex: 1;
        min-height: 0;
      }

      /* Stationary contact shadows keep the transcript beneath the chrome.
         They neither mask the scroll surface nor participate in hit testing. */
      .transcriptColumn > dees-harness-message-list::before,
      .transcriptColumn > dees-harness-message-list::after {
        content: '';
        position: absolute;
        inset-inline: 0;
        height: 20px;
        z-index: 1;
        pointer-events: none;
      }

      .transcriptColumn > dees-harness-message-list::before {
        content: none;
        top: 0;
      }

      .toolbar + .chatBody .transcriptColumn > dees-harness-message-list::before {
        content: '';
        background: linear-gradient(to bottom,
          var(--dees-color-scroll-edge-shadow),
          color-mix(in srgb, var(--dees-color-scroll-edge-shadow) 35%, transparent) 6px,
          transparent 100%);
      }

      .transcriptColumn > dees-harness-message-list::after {
        bottom: 0;
        background: linear-gradient(to top,
          var(--dees-color-scroll-edge-shadow),
          color-mix(in srgb, var(--dees-color-scroll-edge-shadow) 35%, transparent) 6px,
          transparent 100%);
      }

      /* Dock only within the conversation column, above transcript content. */
      .composerRow {
        flex: 0 0 auto;
        position: relative;
        z-index: 1;
      }

      .composerContextBar {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 8px 12px 0;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 16px;
      }

      .composerContextBar dees-icon {
        flex: 0 0 auto;
        color: var(--dees-color-text-secondary);
      }

      .composerContextLabel {
        flex: 0 0 auto;
      }

      .composerContextTitle {
        min-width: 0;
        overflow: hidden;
        color: var(--dees-color-text-secondary);
        font-weight: 600;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ];

  // ---------------------------------------------------------------- methods

  /** Streams an incremental update into the message list. */
  public applyDelta(delta: THarnessStreamDelta): void {
    this.listElement?.applyDelta(delta);
    if (!delta.parentMessageId && delta.type === 'tool-update') this.requestUpdate();
  }

  /** Reconciles direct host mutations while retaining the canonical message array. */
  public refreshMessages(messageIdsArg?: readonly string[]): void {
    if (messageIdsArg?.length === 0) return;
    this.listElement?.refreshMessages(messageIdsArg);
    this.requestUpdate();
  }

  public async focusComposer(): Promise<void> {
    await this.updateComplete;
    await this.composerElement?.focusPrompt();
  }

  public clearComposer(): void {
    this.composerValue = '';
    this.composerElement?.clear();
  }

  public openAttachmentPicker(): boolean {
    return this.composerElement?.openAttachmentPicker() ?? false;
  }

  public async openModelSelector(): Promise<boolean> {
    await this.updateComplete;
    return this.composerElement?.openModelSelector() ?? false;
  }

  public async focusPendingPermission(): Promise<boolean> {
    await this.updateComplete;
    return this.listElement?.focusPendingPermission() ?? false;
  }

  public scrollToBottom(force = false): void {
    this.listElement?.scrollToBottom(force);
  }

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    await this.updateComplete;
    if (!this.isConnected) return;
    this.syncSessionLayout(this.getBoundingClientRect().width);
    this.sessionLayoutResizeObserver?.disconnect();
    this.sessionLayoutResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) this.syncSessionLayout(entry.contentRect.width);
    });
    this.sessionLayoutResizeObserver.observe(this);
  }

  public async disconnectedCallback(): Promise<void> {
    this.sessionLayoutResizeObserver?.disconnect();
    this.sessionLayoutResizeObserver = undefined;
    await super.disconnectedCallback();
  }

  private syncSessionLayout(widthArg: number): void {
    const narrow = widthArg <= 680;
    if (narrow === this.narrowSessionLayout) return;
    this.narrowSessionLayout = narrow;
    this.sessionSidebarOpen = false;
  }

  public get effectiveTodos(): IHarnessTodoItem[] {
    if (this.todosAuthoritative || this.todos.length) return this.todos;
    // the newest todo call is the current state — an empty write clears
    // the panel instead of falling back to an older list
    for (let index = this.messages.length - 1; index >= 0; index--) {
      const call = this.messages[index]?.toolCall;
      if (!call) continue;
      if (this.toolRegistry.resolve(call).kind !== 'todo') continue;
      return harnessParseTodos(call.input);
    }
    return [];
  }

  /** Top-level tool messages still present in the host's retained transcript window. */
  public get toolActivityMessages(): IHarnessMessage[] {
    return this.messages.filter((message) => Boolean(message.toolCall));
  }

  public get hasSessionSidebar(): boolean {
    if (!this.showSessionSidebar) return false;
    return (
      (this.showTodosPanel && (this.todosAuthoritative || this.effectiveTodos.length > 0))
      || this.toolActivityMessages.length > 0
      || Object.values(this.sessionMetrics).some((value) => value !== undefined)
      || this.scratchpad !== undefined
      || this.sessionIntelligenceEnabled
    );
  }

  private get listElement(): DeesHarnessMessageList | undefined {
    const element = this.shadowRoot?.querySelector('dees-harness-message-list');
    return element instanceof DeesHarnessMessageList ? element : undefined;
  }

  private get composerElement(): DeesHarnessComposer | undefined {
    const element = this.shadowRoot?.querySelector('dees-harness-composer');
    return element instanceof DeesHarnessComposer ? element : undefined;
  }

  private get sessionSidebarElement(): DeesHarnessSessionSidebar | undefined {
    const element = this.shadowRoot?.querySelector('dees-harness-session-sidebar');
    return element instanceof DeesHarnessSessionSidebar ? element : undefined;
  }

  private readonly toggleSessionSidebar = (): void => {
    if (!this.narrowSessionLayout) {
      this.desktopSessionSidebarVisible = !this.desktopSessionSidebarVisible;
      return;
    }
    if (this.sessionSidebarOpen) {
      void this.closeSessionSidebar();
      return;
    }
    void this.openSessionSidebar();
  };

  /** Reveals session details and optionally expands a specific available section. */
  public async openSessionSidebar(
    sectionArg?: THarnessSessionSidebarSection,
  ): Promise<boolean> {
    if (!this.hasSessionSidebar) return false;
    const initialSidebar = this.sessionSidebarElement;
    if (sectionArg && initialSidebar && !initialSidebar.hasSection(sectionArg)) return false;
    if (this.narrowSessionLayout) this.sessionSidebarOpen = true;
    else this.desktopSessionSidebarVisible = true;
    await this.updateComplete;
    const sidebar = this.sessionSidebarElement;
    if (sectionArg) return sidebar?.openSection(sectionArg) ?? false;
    const panel = this.shadowRoot?.querySelector<HTMLElement>('.sessionPanel');
    if (this.narrowSessionLayout) {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      panel?.focus();
    }
    return true;
  }

  private async closeSessionSidebar(): Promise<void> {
    this.sessionSidebarOpen = false;
    await this.updateComplete;
    const toggleButton = this.shadowRoot?.querySelector<HTMLButtonElement>(
      this.showToolbar ? '.toolbarSessionPanelToggle' : '.sessionPanelToggle',
    );
    toggleButton?.focus();
  }

  // ----------------------------------------------------------------- render

  public render(): TemplateResult {
    return html`
      ${this.showToolbar ? this.renderToolbar() : ''}
      <div class="chatBody">
        <div class="transcriptColumn">
          <dees-harness-message-list
            .messages=${this.messages}
            .permissions=${this.permissions}
            .permissionsBusy=${this.disabled}
            .questions=${this.questions}
            .status=${this.status}
            .transcriptKey=${this.transcriptKey}
            .hasEarlierMessages=${this.hasEarlierMessages}
            .loadingEarlier=${this.loadingEarlier}
            .showRoles=${this.showRoles}
            .showTimestamps=${this.showTimestamps}
            .markdownWhileStreaming=${this.markdownWhileStreaming}
            .toolRegistry=${this.toolRegistry}
          ></dees-harness-message-list>
          <slot name="composer-status"></slot>
          ${this.renderComposer()}
          ${this.hasSessionSidebar && !this.showToolbar ? html`
            <button
              class="sessionPanelToggle"
              type="button"
              aria-label="Session details"
              title="Session details"
              aria-expanded=${String(this.sessionSidebarOpen)}
              aria-controls="harness-session-panel"
              @click=${this.toggleSessionSidebar}
            >
              <dees-icon icon="lucide:PanelRight" iconSize="16"></dees-icon>
            </button>
          ` : ''}
        </div>
        ${this.hasSessionSidebar
          ? html`
              <button
                class=${`sessionPanelBackdrop ${this.sessionSidebarOpen ? 'open' : ''}`}
                aria-label="Close session details"
                aria-hidden=${String(!this.sessionSidebarOpen)}
                ?inert=${!this.sessionSidebarOpen}
                tabindex=${this.sessionSidebarOpen ? '0' : '-1'}
                @click=${() => void this.closeSessionSidebar()}
              ></button>
              <aside
                id="harness-session-panel"
                class=${`todosPanel sessionPanel ${this.sessionSidebarOpen ? 'open' : ''} ${this.desktopSessionSidebarVisible ? '' : 'desktopHidden'}`}
                aria-label="Session details"
                aria-hidden=${String(this.narrowSessionLayout
                  ? !this.sessionSidebarOpen
                  : !this.desktopSessionSidebarVisible)}
                ?inert=${this.narrowSessionLayout
                  ? !this.sessionSidebarOpen
                  : !this.desktopSessionSidebarVisible}
                tabindex="-1"
                @keydown=${(event: KeyboardEvent) => {
                  if (event.key !== 'Escape' || !this.narrowSessionLayout) return;
                  event.preventDefault();
                  event.stopPropagation();
                  void this.closeSessionSidebar();
                }}
              >
                <dees-button
                  class="sessionPanelClose"
                  size="icon"
                  type="ghost"
                  icon="lucide:X"
                  text="Close session details"
                  title="Close session details"
                  @clicked=${() => void this.closeSessionSidebar()}
                ></dees-button>
                <dees-harness-session-sidebar
                  .todos=${this.effectiveTodos}
                  .showTasks=${this.showTodosPanel}
                  .toolMessages=${this.toolActivityMessages}
                  .toolRegistry=${this.toolRegistry}
                  .metrics=${this.sessionMetrics}
                  .scratchpad=${this.scratchpad}
                  .scratchpadBusy=${this.scratchpadBusy}
                  .scratchpadError=${this.scratchpadError}
                  .intelligenceEnabled=${this.sessionIntelligenceEnabled}
                  .intelligenceAvailabilityStatus=${this.intelligenceAvailabilityStatus}
                  .intelligenceUnavailableReason=${this.intelligenceUnavailableReason}
                  .intelligenceHeading=${this.intelligenceHeading}
                  .intelligenceExchanges=${this.intelligenceExchanges}
                  .intelligenceBusy=${this.intelligenceBusy}
                  .intelligenceError=${this.intelligenceError}
                  .sessionKey=${this.transcriptKey}
                  .disabled=${this.disabled}
                ></dees-harness-session-sidebar>
              </aside>
            `
          : ''}
      </div>
    `;
  }

  private renderComposer(): TemplateResult {
    return html`
      <div class="composerRow">
        <dees-harness-composer
          docked
          .contextDescription=${this.heading ? `Replying to ${this.heading}` : ''}
          .value=${this.composerValue}
          .attachments=${this.attachments}
          .attachmentsEnabled=${this.attachmentsEnabled}
          .suggestions=${this.suggestions}
          .maxAttachmentCount=${this.maxAttachmentCount}
          .maxAttachmentBytes=${this.maxAttachmentBytes}
          .maxTotalAttachmentBytes=${this.maxTotalAttachmentBytes}
          .disabled=${this.disabled}
          .inputLocked=${this.inputLocked}
          .busy=${this.busy || this.status.type === 'busy'}
          .abortEnabled=${this.abortEnabled}
          .abortDisabledReason=${this.abortDisabledReason}
          .queuedCount=${this.queuedCount}
          .steeringEnabled=${this.steeringEnabled}
          .queueingEnabled=${this.queueingEnabled}
          .inputSteeringEnabled=${this.inputSteeringEnabled}
          .account=${this.account}
          .accountOptions=${this.accountOptions}
          .model=${this.model}
          .modelOptions=${this.modelOptions}
          .reasoningEffort=${this.reasoningEffort}
          .effortOptions=${this.effortOptions}
          .mode=${this.mode}
          .modeOptions=${this.modeOptions}
          @harness-input=${(event: CustomEvent<{ value: string }>) => {
            this.composerValue = event.detail?.value ?? '';
          }}
          @harness-attachments-change=${(event: CustomEvent<{ attachments: IHarnessAttachment[] }>) => {
            this.attachments = event.detail?.attachments ?? [];
          }}
          @harness-account-change=${(event: CustomEvent<IHarnessAccountChangeDetail>) => {
            if (event.detail?.account) this.account = event.detail.account;
          }}
          @harness-model-change=${(event: CustomEvent<{ model?: string; reasoningEffort?: string }>) => {
            if (event.detail?.model) this.model = event.detail.model;
            if (event.detail?.reasoningEffort) this.reasoningEffort = event.detail.reasoningEffort;
          }}
          @harness-mode-change=${(event: CustomEvent<{ mode?: string }>) => {
            if (event.detail?.mode) this.mode = event.detail.mode;
          }}
        >
          ${this.heading
            ? html`
                <div slot="context" class="composerContextBar" title=${`Replying to ${this.heading}`}>
                  <dees-icon icon="lucide:MessageSquare" iconSize="11" aria-hidden="true"></dees-icon>
                  <span class="composerContextLabel">Replying to</span>
                  <span class="composerContextTitle">${this.heading}</span>
                </div>
              `
            : ''}
        </dees-harness-composer>
      </div>
    `;
  }

  private renderToolbar(): TemplateResult {
    return html`
      <div class="toolbar">
        <div class="headingBlock">
          <div class="headingText">${this.heading || 'Agent'}</div>
          ${this.subheading ? html`<div class="subheadingText">${this.subheading}</div>` : ''}
        </div>
        <div class="toolbarActions">
          <dees-harness-usage compact .usage=${this.usage}></dees-harness-usage>
          ${this.toolbarActions.map((actionArg) => {
            return html`
              <dees-button
                size="icon"
                type="ghost"
                .icon=${actionArg.iconName}
                .text=${actionArg.label}
                .disabled=${Boolean(actionArg.disabled)}
                title=${actionArg.tooltip ?? actionArg.label}
                @clicked=${() => actionArg.action()}
              ></dees-button>
            `;
          })}
          ${this.hasSessionSidebar ? html`
            <button
              class="toolbarSessionPanelToggle"
              type="button"
              aria-label="Toggle session details"
              title="Toggle session details"
              aria-expanded=${String(this.narrowSessionLayout
                ? this.sessionSidebarOpen
                : this.desktopSessionSidebarVisible)}
              aria-controls="harness-session-panel"
              @click=${this.toggleSessionSidebar}
            >
              <dees-icon
                icon=${this.narrowSessionLayout
                  ? (this.sessionSidebarOpen ? 'lucide:PanelRightClose' : 'lucide:PanelRightOpen')
                  : (this.desktopSessionSidebarVisible ? 'lucide:PanelRightClose' : 'lucide:PanelRightOpen')}
                iconSize="16"
              ></dees-icon>
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }
}
